import { asTranslationKey } from '@/i18n/translate-utils'

/**
 * Exhaustive map of supported MIME types to their i18n translation keys.
 *
 * Every entry uses {@link asTranslationKey} so that the i18next-parser lexer
 * can discover all keys statically when running `pnpm translate:extract`.
 *
 * To add support for a new MIME type:
 * 1. Add an entry here with `asTranslationKey('gallery.mime.<type>')`.
 * 2. Add the English string to `i18n/en_US.json` with a `gallery.mime.<type>` key.
 * 3. Run `pnpm translate:extract` to propagate the key to all other locale files.
 */
export const MIME_TRANSLATION_KEYS = {
  // Images
  'image/jpeg': asTranslationKey('gallery.mime.image/jpeg'),
  'image/png': asTranslationKey('gallery.mime.image/png'),
  'image/gif': asTranslationKey('gallery.mime.image/gif'),
  'image/webp': asTranslationKey('gallery.mime.image/webp'),
  'image/svg+xml': asTranslationKey('gallery.mime.image/svg+xml'),
  // Video
  'video/mp4': asTranslationKey('gallery.mime.video/mp4'),
  'video/webm': asTranslationKey('gallery.mime.video/webm'),
  'video/ogg': asTranslationKey('gallery.mime.video/ogg'),
  // Audio
  'audio/mpeg': asTranslationKey('gallery.mime.audio/mpeg'),
  'audio/ogg': asTranslationKey('gallery.mime.audio/ogg'),
  'audio/wav': asTranslationKey('gallery.mime.audio/wav'),
  // Documents
  'application/pdf': asTranslationKey('gallery.mime.application/pdf'),
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': asTranslationKey(
    'gallery.mime.application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ),
  'application/msword': asTranslationKey('gallery.mime.application/msword'),
  'text/plain': asTranslationKey('gallery.mime.text/plain'),
  // Spreadsheets
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': asTranslationKey(
    'gallery.mime.application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ),
  'application/vnd.ms-excel': asTranslationKey('gallery.mime.application/vnd.ms-excel'),
  // Presentations
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': asTranslationKey(
    'gallery.mime.application/vnd.openxmlformats-officedocument.presentationml.presentation',
  ),
  'application/vnd.ms-powerpoint': asTranslationKey('gallery.mime.application/vnd.ms-powerpoint'),
  // Archives
  'application/zip': asTranslationKey('gallery.mime.application/zip'),
  'application/x-zip-compressed': asTranslationKey('gallery.mime.application/x-zip-compressed'),
  'application/x-rar-compressed': asTranslationKey('gallery.mime.application/x-rar-compressed'),
  'application/x-7z-compressed': asTranslationKey('gallery.mime.application/x-7z-compressed'),
  // Code / markup
  'text/html': asTranslationKey('gallery.mime.text/html'),
  'text/css': asTranslationKey('gallery.mime.text/css'),
  'text/javascript': asTranslationKey('gallery.mime.text/javascript'),
  'application/json': asTranslationKey('gallery.mime.application/json'),
  'application/xml': asTranslationKey('gallery.mime.application/xml'),
  'text/xml': asTranslationKey('gallery.mime.text/xml'),
} as const satisfies Record<string, string>

/** Union of every supported MIME type string. */
export type SupportedMimeType = keyof typeof MIME_TRANSLATION_KEYS
